import { computed } from 'vue';
import { getDisplayConfig, getMetricsByCategory, type MetricCategory, type MetricDefinition, METRICS } from '@/config/metricsConfig';
import type {
  LocationMetricsProps,
  MetricAmount,
  MetricComparisonProps,
  MetricValue,
  ProcessedMetric,
  TotalMetricsProps
} from '@/models/metricDefinitions';
import type { UnifiedLocationMetrics, UnifiedMetrics } from '@/models/metrics';
import type { SubMetricConfig } from '@/config/viewConfig';
import type { LoadingStates } from '@/models/loadingStates';

type MetricLoadingStates = Record<string, LoadingStates>;

const readNumber = (locationData: UnifiedLocationMetrics, path: string): number => {
  const value = (locationData as unknown as Record<string, unknown>)?.[path];
  return typeof value === 'number' ? value : 0;
};

const sumPaths = (locationData: UnifiedLocationMetrics, paths: readonly string[]): number =>
  paths.reduce((sum, path) => sum + readNumber(locationData, path), 0);

const toPercentage = (part: number, whole: number): number => (whole === 0 ? 0 : (part / whole) * 100);

// Averages are rounded to the precision they are displayed with, so totals match the per-location values
const roundToDisplayPrecision = (value: number, metric: MetricDefinition): number => {
  const decimalPlaces = metric.decimalPlaces ?? (metric.formatAsCurrency ? 2 : 0);
  return Number(value.toFixed(decimalPlaces));
};

const selectMetrics = (category?: MetricCategory, metricKeys?: string[]): MetricDefinition[] => {
  if (metricKeys) {
    // Use the exact metrics specified by keys, in the given order
    return METRICS.filter((metric) => metricKeys.includes(metric.key)).sort(
      (a, b) => metricKeys.indexOf(a.key) - metricKeys.indexOf(b.key)
    );
  }

  if (category) {
    return getMetricsByCategory(category);
  }

  throw new Error('Either category or metricKeys must be provided');
};

export const useMetrics = () => {
  const resolveLoading = (
    locationName: string,
    metric: MetricDefinition,
    loadingStates: LoadingStates,
    metricLoadingStates?: MetricLoadingStates
  ): boolean => {
    const independent = metricLoadingStates?.[metric.key]?.[locationName];
    return independent ?? loadingStates[locationName] ?? false;
  };

  const getMetricValue = (locationData: UnifiedLocationMetrics, metric: MetricDefinition): MetricAmount => {
    if (metric.shareOf) {
      return toPercentage(readNumber(locationData, metric.dataPath), sumPaths(locationData, metric.shareOf));
    }

    try {
      const value = (locationData as unknown as Record<string, unknown>)?.[metric.dataPath];
      if (metric.formatAsDate) {
        if (value === null) {
          return null;
        }
        if (value instanceof Date) {
          return value;
        }
      }
      return typeof value === 'number' ? value : 0;
    } catch {
      return metric.formatAsDate ? null : 0;
    }
  };

  const getMetricTarget = (locationData: UnifiedLocationMetrics, metric: MetricDefinition): number | '?' | undefined => {
    if (!metric.targetPath) return undefined;

    try {
      const target = (locationData as unknown as Record<string, unknown>)?.[metric.targetPath];
      if (typeof target === 'number') return target;
      if (target === null) return '?';
      return undefined;
    } catch {
      return undefined;
    }
  };

  const asSubMetric = (metric: MetricDefinition): MetricDefinition | undefined =>
    metric.subDataPath ? { ...metric, dataPath: metric.subDataPath } : undefined;

  const asSubAmount = (value: MetricAmount): number | undefined => (typeof value === 'number' ? value : undefined);

  const getSubValue = (locationData: UnifiedLocationMetrics, metric: MetricDefinition): number | undefined => {
    const subMetric = asSubMetric(metric);
    return subMetric ? asSubAmount(getMetricValue(locationData, subMetric)) : undefined;
  };

  const createProcessedMetric = (locationData: UnifiedLocationMetrics, isLoading: boolean, metric: MetricDefinition): ProcessedMetric => {
    const value: MetricValue = {
      amount: getMetricValue(locationData, metric),
      target: getMetricTarget(locationData, metric),
      subAmount: getSubValue(locationData, metric),
      isLoading
    };

    return {
      key: metric.key,
      value,
      config: getDisplayConfig(metric)
    };
  };

  const calculateAggregatedValue = (dataValues: UnifiedMetrics, metric: MetricDefinition): MetricAmount => {
    if (metric.formatAsDate) {
      // Return the most recent date in the list of values
      const values = Object.values(dataValues);
      const dates = values.map((data) => getMetricValue(data, metric)).filter((val): val is Date => val instanceof Date);

      return dates.length > 0 ? dates.reduce((latest, current) => (current > latest ? current : latest), new Date(0)) : null;
    }

    const values = Object.values(dataValues);

    if (metric.shareOf) {
      // Percentages can't be summed or averaged, so the total is the share of the summed values
      const shareOf = metric.shareOf;
      const part = values.reduce((sum, data) => sum + readNumber(data, metric.dataPath), 0);
      const whole = values.reduce((sum, data) => sum + sumPaths(data, shareOf), 0);
      return toPercentage(part, whole);
    }

    if (metric.aggregationType === 'average' && metric.weightPath) {
      // Weighted, so locations with more e.g. paying students count accordingly
      const weightPath = metric.weightPath;
      const weightedSum = values.reduce((sum, data) => sum + readNumber(data, metric.dataPath) * readNumber(data, weightPath), 0);
      const totalWeight = values.reduce((sum, data) => sum + readNumber(data, weightPath), 0);
      return totalWeight > 0 ? roundToDisplayPrecision(weightedSum / totalWeight, metric) : 0;
    }

    if (metric.aggregationType === 'average') {
      const validValues = values
        .map((data) => getMetricValue(data, metric))
        .filter((val): val is number => typeof val === 'number' && val > 0);
      return validValues.length > 0
        ? roundToDisplayPrecision(validValues.reduce((sum, val) => sum + val, 0) / validValues.length, metric)
        : 0;
    }

    // Default to sum
    return values.reduce((sum, data) => {
      const value = getMetricValue(data, metric);
      return sum + (typeof value === 'number' ? value : 0);
    }, 0);
  };

  const calculateAggregatedSubValue = (dataValues: UnifiedMetrics, metric: MetricDefinition): number | undefined => {
    const subMetric = asSubMetric(metric);
    return subMetric ? asSubAmount(calculateAggregatedValue(dataValues, subMetric)) : undefined;
  };

  const buildLocationEntry = (
    locationName: string,
    locationData: UnifiedLocationMetrics,
    metricsToShow: MetricDefinition[],
    loadingStates: LoadingStates,
    metricLoadingStates?: MetricLoadingStates
  ) => ({
    locationName,
    metrics: metricsToShow.map((metric) =>
      createProcessedMetric(locationData, resolveLoading(locationName, metric, loadingStates, metricLoadingStates), metric)
    )
  });

  const createLocationMetrics = (
    metrics: UnifiedMetrics,
    loadingStates: LoadingStates,
    category?: MetricCategory,
    metricKeys?: string[],
    metricLoadingStates?: MetricLoadingStates
  ) => {
    return computed((): LocationMetricsProps['locationData'] => {
      const metricsToShow = selectMetrics(category, metricKeys);

      return Object.entries(metrics).map(([locationName, locationData]) =>
        buildLocationEntry(locationName, locationData, metricsToShow, loadingStates, metricLoadingStates)
      );
    });
  };

  const computeAggregatedTarget = (metric: MetricDefinition, metrics: UnifiedMetrics): number | '?' | undefined => {
    if (!metric.targetPath) return undefined;
    const targets = Object.values(metrics).map((locationData) => getMetricTarget(locationData, metric));
    if (targets.includes('?')) return '?';
    return targets.reduce<number>((sum, t) => sum + (typeof t === 'number' ? t : 0), 0);
  };

  const buildTotalMetricEntry = (
    metric: MetricDefinition,
    metrics: UnifiedMetrics,
    loadingStates: LoadingStates,
    locationNames: string[],
    metricLoadingStates?: MetricLoadingStates
  ): TotalMetricsProps['metrics'][number] => ({
    value: calculateAggregatedValue(metrics, metric),
    target: computeAggregatedTarget(metric, metrics),
    subValue: calculateAggregatedSubValue(metrics, metric),
    config: getDisplayConfig(metric),
    isLoading: locationNames.some((locationName) => resolveLoading(locationName, metric, loadingStates, metricLoadingStates))
  });

  const createTotalMetrics = (
    metrics: UnifiedMetrics,
    loadingStates: LoadingStates,
    category: MetricCategory = 'dashboard',
    metricKeys?: string[],
    metricLoadingStates?: MetricLoadingStates
  ) => {
    return computed((): TotalMetricsProps['metrics'] => {
      const metricsToShow = selectMetrics(category, metricKeys);
      const locationNames = Object.keys(metrics);

      return metricsToShow.map((metric) => buildTotalMetricEntry(metric, metrics, loadingStates, locationNames, metricLoadingStates));
    });
  };

  const findMetric = (metricKey: string): MetricDefinition => {
    const metric = METRICS.find((m) => m.key === metricKey);
    if (!metric) {
      throw new Error(`Metric with key "${metricKey}" not found`);
    }

    return metric;
  };

  const createComparisonMetrics = (
    metrics: UnifiedMetrics,
    loadingStates: LoadingStates,
    metricKey: string,
    metricLoadingStates?: MetricLoadingStates,
    subMetric?: SubMetricConfig
  ) => {
    return computed((): MetricComparisonProps => {
      const metric = findMetric(metricKey);
      const secondary = subMetric ? findMetric(subMetric.metricKey) : undefined;

      const buildValue = (data: UnifiedLocationMetrics, name: string, definition: MetricDefinition): MetricValue => ({
        amount: getMetricValue(data, definition),
        target: getMetricTarget(data, definition),
        subAmount: getSubValue(data, definition),
        isLoading: resolveLoading(name, definition, loadingStates, metricLoadingStates)
      });

      const locations = Object.entries(metrics).map(([name, data]) => ({
        name,
        value: buildValue(data, name, metric),
        subValue: secondary ? buildValue(data, name, secondary) : undefined
      }));

      const buildTotalLabel = (definition: MetricDefinition): string =>
        definition.aggregationType === 'average' ? 'Durchschnitt' : 'Gesamt';

      const buildTotal = (definition: MetricDefinition): MetricValue => ({
        amount: calculateAggregatedValue(metrics, definition),
        target: computeAggregatedTarget(definition, metrics),
        subAmount: calculateAggregatedSubValue(metrics, definition),
        isLoading: locations.some((location) => location.value.isLoading)
      });

      return {
        metricName: metric.key,
        locations,
        config: getDisplayConfig(metric),
        subConfig: secondary ? { ...getDisplayConfig(secondary), label: subMetric!.label ?? secondary.label, icon: undefined } : undefined,
        total: buildTotal(metric),
        totalLabel: buildTotalLabel(metric),
        subTotal: secondary ? buildTotal(secondary) : undefined
      };
    });
  };

  return {
    createLocationMetrics,
    createTotalMetrics,
    createComparisonMetrics,
    getMetricValue,
    getMetricTarget,
    calculateAggregatedValue
  };
};
